import { useState, useEffect } from 'react'
import breakpoints from '@/shared/json-styles/breakpoints.json'

/**
 * Custom hook to track breakpoint changes reactively
 * Updates on window resize without requiring page refresh
 *
 * @param {string|number} breakpoint - Breakpoint name or custom pixel value
 *   Named breakpoints: 'smallmobile' (≤360px), 'mobile' (≤768px), 'tablet' (≤1023px),
 *                      'desktop' (≤1215px), 'widescreen' (≤1919px), 'fullhd' (≤36000px)
 *   Custom: Pass a number for a custom pixel breakpoint (e.g., 640)
 * @returns {boolean} True if viewport is at or below the specified breakpoint
 *
 * @example
 * const isMobile = useBreakpoint('mobile')
 * const isTablet = useBreakpoint('tablet')
 * const isCustom = useBreakpoint(640)
 * if (isMobile) return <MobileView />
 */
const useBreakpoint = (breakpoint) => {
  const checkBreakpoint = () => {
    // Handle custom pixel value
    if (typeof breakpoint === 'number') {
      return window.innerWidth <= breakpoint
    }

    // Handle named breakpoint
    if (!breakpoints[breakpoint]) {
      // eslint-disable-next-line no-console
      console.warn(`Breakpoint "${breakpoint}" not found. Available: ${Object.keys(breakpoints).join(', ')}`)
      return false
    }
    return window.innerWidth <= parseInt(breakpoints[breakpoint].end)
  }

  const [matches, setMatches] = useState(checkBreakpoint)

  useEffect(() => {
    const handleResize = () => {
      setMatches(checkBreakpoint())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [breakpoint])

  return matches
}

export default useBreakpoint
